import { DateTime } from 'luxon';

import { ConflictError } from '@cdaas/api-clients/dist/asset-user-openapi';

import { User } from '../../../../types';
import { isValidEmail, isValidPhoneNumberWithCountryCallingCode } from '../../../../utils';
import { ErrorTypes, UserWithPossibleError } from '../utils';

export interface ErrorTranslations {
  phoneNumberMissing: string;
  emailMissing: string;
  phoneNumberInvalid: string;
  emailInvalid: string;
  phoneNumberDuplicate: string;
  emailDuplicate: string;
  nonExistingUserGroup: string;
  preScheduledOnboardingAtInvalid: string;
  preScheduledOnboardingAtInPast: string;
}

export const getUsersWithPossibleErrors = (
  users: User[],
  errorTranslations: ErrorTranslations,
  backendConflictError?: ConflictError['error'],
  backendConflictErrorMessage?: string,
): UserWithPossibleError[] => {
  /* Expecting the possible backend returned conflict errors to be in the following format:
  {
    "error": "phone_number_already_in_use",
    "message": "Phone number(s) provided are already in use. Conflicting phone number(s): '+3584512345678, +3585012345678, +3584412345678'."
  }
  OR
  {
    "error": "email_already_in_use",
    "message": "Email address(es) provided are already in use. Conflicting email(s): 'foo1@example.com, bar1@example.com, baz1@example.com'."
  }
  Rather error-prone to parse the emails and phone numbers from an error message string
  and backend should at some point be updated to return a more structured error object.
  */
  const getConflictingPhonenumbersOrEmails = (backendConflictErrorMessage?: string) => {
    if (!backendConflictErrorMessage) return [];
    return backendConflictErrorMessage
      .substring(backendConflictErrorMessage.indexOf("'") + 1, backendConflictErrorMessage.lastIndexOf("'"))
      .trim()
      .split(',');
  };

  let existingUsersDuplicatePhoneNumbers: string[] = [];
  let existingUsersDuplicateEmails: string[] = [];
  if (backendConflictError && backendConflictError === 'phone_number_already_in_use') {
    existingUsersDuplicatePhoneNumbers = getConflictingPhonenumbersOrEmails(backendConflictErrorMessage);
  }
  if (backendConflictError && backendConflictError === 'email_already_in_use') {
    existingUsersDuplicateEmails = getConflictingPhonenumbersOrEmails(backendConflictErrorMessage);
  }

  const allToBeImportedPhoneNumbers = users.map(user => user.phone_number);
  const allToBeImportedEmails = users.map(user => user.email);

  const checkDuplicate = (arr: (string | undefined)[], value: string | undefined) => {
    return arr.filter(valueToCheck => value && valueToCheck?.toLowerCase() === value?.toLowerCase()).length > 1;
  };

  const isPreScheduledOnboardingAtInvalid = (value: string | null | undefined): boolean => {
    if (!value) return false;

    const date = DateTime.fromISO(value);
    return !date.isValid;
  };

  const isPreScheduledOnboardingInPast = (value: string | null | undefined): boolean => {
    if (isPreScheduledOnboardingAtInvalid(value)) return false;

    const date = DateTime.fromISO(value ?? '');
    const today = DateTime.now().startOf('day');
    return date < today;
  };

  return users.map(user => {
    let error;
    if (!user.phone_number) {
      error = { errorType: ErrorTypes.PhoneNumberMissing, errorMessage: errorTranslations.phoneNumberMissing };
    } else if (!user.email) {
      error = { errorType: ErrorTypes.EmailMissing, errorMessage: errorTranslations.emailMissing };
    } else if (!isValidPhoneNumberWithCountryCallingCode(user.phone_number)) {
      error = { errorType: ErrorTypes.PhoneNumberInvalid, errorMessage: errorTranslations.phoneNumberInvalid };
    } else if (!isValidEmail(user.email)) {
      error = { errorType: ErrorTypes.EmailInvalid, errorMessage: errorTranslations.emailInvalid };
    } else if (checkDuplicate(allToBeImportedPhoneNumbers, user.phone_number)) {
      error = { errorType: ErrorTypes.PhoneNumberDuplicateBetweenToBeImportedUsers, errorMessage: errorTranslations.phoneNumberDuplicate };
    } else if (checkDuplicate(allToBeImportedEmails, user.email)) {
      error = { errorType: ErrorTypes.EmailDuplicateBetweenToBeImportedUsers, errorMessage: errorTranslations.emailDuplicate };
    } else if (
      existingUsersDuplicatePhoneNumbers.find(existingPhoneNumber => existingPhoneNumber.toLowerCase() === user.phone_number?.toLowerCase())
    ) {
      error = { errorType: ErrorTypes.PhoneNumberDuplicateBetweenExistingUser, errorMessage: errorTranslations.phoneNumberDuplicate };
    } else if (existingUsersDuplicateEmails.find(existingEmail => existingEmail.toLowerCase() === user.email?.toLowerCase())) {
      error = { errorType: ErrorTypes.EmailDuplicateBetweenExistingUser, errorMessage: errorTranslations.emailDuplicate };
    } else if (!user.user_group?.id) {
      error = { errorType: ErrorTypes.NonExistingUserGroup, errorMessage: errorTranslations.nonExistingUserGroup };
    } else if (isPreScheduledOnboardingAtInvalid(user.prescheduled_onboarding_at)) {
      error = { errorType: ErrorTypes.PreScheduledOnboardingAtInvalid, errorMessage: errorTranslations.preScheduledOnboardingAtInvalid };
    } else if (isPreScheduledOnboardingInPast(user.prescheduled_onboarding_at)) {
      error = { errorType: ErrorTypes.PreScheduledOnboardingAtInPast, errorMessage: errorTranslations.preScheduledOnboardingAtInPast };
    }

    return { ...user, error };
  });
};
